import { useState } from "react";
import TextInput from "../components/TextInput";
import { validateForm, ValidationError } from "../utils/validation";
import { getFirestore, setDoc, doc, getDoc } from 'firebase/firestore';
import { useFirebase } from '../contexts/FirebaseContext';
import ReactConfetti from 'react-confetti';
import { useAuth } from '../contexts/AuthContext';
import { convertToSnakeCase } from '../utils/validation';
import { EMAIL } from '../utils/constants';
import TextArea from "../components/TextArea";
import { COUNTRIES, getRegionsByCountry } from '../constants/locations';
import Dropdown from "../components/Dropdown";
import ImageUpload from "../components/ImageUpload";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { formatUrl } from '../utils/tools';
import { providerValidationSchema } from '../validations/providerValidationSchema';
import { normalizeSocialHandle } from '../utils/socialMedia';
import { encode } from 'blurhash';
import EventTypeSelect from '../components/EventTypeSelect';
import Button from '../components/Buttons/Button';

type FormData = {
  provider_name: string;
  provider_logo: string;
  provider_logo_blur_hash: string;
  provider_instagram: string;
  providers_tiktok: string;
  provider_link: string;
  provider_country: string;
  provider_region: string;
  provider_postcode: string;
  provider_email: string;
  provider_description: string;
  provider_types: string[];
};

const getImageData = (image: HTMLImageElement): Promise<ImageData> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    canvas.width = image.width;
    canvas.height = image.height;
    const context = canvas.getContext("2d");
    if (!context) reject(new Error("Could not get canvas context"));
    
    context!.drawImage(image, 0, 0);
    resolve(context!.getImageData(0, 0, image.width, image.height));
  });
};

const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

const SubmitProvider = () => {
  const {app, storage} = useFirebase();
  const { currentUser } = useAuth();

  const [formData, setFormData] = useState<FormData>({
    provider_name: "",
    provider_logo: "",
    provider_logo_blur_hash: "",
    provider_instagram: "",
    providers_tiktok: "",
    provider_link: "",
    provider_country: "",
    provider_region: "",
    provider_postcode: "",
    provider_email: "",
    provider_description: "",
    provider_types: [],
  });

  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [windowDimensions, setWindowDimensions] = useState({ 
    width: window.innerWidth, 
    height: window.innerHeight 
  });

  // Add new state for available regions
  const [availableRegions, setAvailableRegions] = useState<string[]>([]);

  const [providerLogo, setProviderLogo] = useState<File | null>(null);

  // Add new state for tracking logo upload status
  const [logoUploadStatus, setLogoUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  // Add loading state for provider name check
  const [checkingName, setCheckingName] = useState(false);

  const validateField = (fieldName: keyof typeof providerValidationSchema, value: any) => {
    if (touchedFields.has(fieldName) || isSubmitted) {
      const schema = providerValidationSchema[fieldName];
      const validationResult = validateForm(
        { [fieldName]: value },
        { [fieldName]: schema }
      );
      
      // If there's a validation error and a custom message exists, use it
      const updatedValidationResult = validationResult.map(error => {
        if (error.field === fieldName && schema.message) {
          return { ...error, message: schema.message };
        }
        return error;
      });
      
      setErrors(prev => {
        const otherErrors = prev.filter(error => error.field !== fieldName);
        return [...otherErrors, ...updatedValidationResult];
      });
    }
  };

  const handleChange = (fieldName: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    setTouchedFields(prev => new Set(prev).add(fieldName));
    
    if (fieldName !== 'provider_name') {
      validateField(fieldName, value);
    }
  };

  const handleOptionalChange = (fieldName: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  // Modify the country change handler to update regions
  const handleCountryChange = (country: string) => {
    handleOptionalChange('provider_country', country);
    // Reset the region when country changes
    handleOptionalChange('provider_region', '');
    // Update available regions based on selected country
    const regions = getRegionsByCountry(country);
    setAvailableRegions(regions);
  };

  // Modify handleLogoSelect to upload immediately when selected
  const handleLogoSelect = async (file: File) => {
    setLogoUploadStatus('uploading');
    setProviderLogo(file);
    const imageRef = ref(storage, `providers/logos/${Date.now()}_${file.name}`);
    
    try {
      const snapshot = await uploadBytes(imageRef, file);
      const url = await getDownloadURL(snapshot.ref);

      // Generate blurhash
      const objectUrl = URL.createObjectURL(file);
      const img = await loadImage(objectUrl);
      const imageData = await getImageData(img);
      
      const blurhash = encode(
        imageData.data,
        imageData.width,
        imageData.height,
        4,  // x components
        3   // y components
      );

      URL.revokeObjectURL(objectUrl);

      setLogoUrl(url);
      setLogoUploadStatus('success');
      setFormData(prev => ({ 
        ...prev, 
        provider_logo: url,
        provider_logo_blur_hash: blurhash 
      }));
    } catch (error) {
      console.error("Error uploading logo:", error);
      setLogoUploadStatus('error');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    
    // Format the URL before validation
    const formDataWithFormattedUrl = {
      ...formData,
      provider_link: formatUrl(formData.provider_link)
    };
    
    const validationErrors = validateForm(formDataWithFormattedUrl, providerValidationSchema);
    setErrors(validationErrors);

    if (validationErrors.length === 0) {
      try {
        const db = getFirestore(app);
        const providerId = convertToSnakeCase(formData.provider_name);
        
        if (!currentUser?.uid) {
          throw new Error('No authenticated user found');
        }

        const providerData = {
          ...formDataWithFormattedUrl,
          provider_instagram: formData.provider_instagram ? 
            normalizeSocialHandle(formData.provider_instagram, 'instagram') : '',
          providers_tiktok: formData.providers_tiktok ? 
            normalizeSocialHandle(formData.providers_tiktok, 'tiktok') : '',
          id: providerId,
          created_at: new Date(),
          approved: false,
          owner_id: currentUser?.uid,
          provider_logo_blur_hash: formData.provider_logo_blur_hash
        };

        await setDoc(doc(db, 'providers', providerId), providerData);
        
        setShowConfetti(true);
        setShowSuccessModal(true);
        
        // Reset form and states
        setFormData({
          provider_name: "",
          provider_logo: "",
          provider_logo_blur_hash: "",
          provider_instagram: "",
          providers_tiktok: "",
          provider_link: "",
          provider_country: "",
          provider_region: "",
          provider_postcode: "",
          provider_email: "",
          provider_description: "",
          provider_types: [],
        });
        setTouchedFields(new Set());
        setIsSubmitted(false);
        setLogoUploadStatus('idle');
        setLogoUrl(null);
        
        setTimeout(() => {
          setShowConfetti(false);
        }, 500);
        
      } catch (error: any) {
        console.error('Error adding provider:', error);
        
        if (error.code === 'permission-denied') {
          setErrors([{
            field: 'provider_name',
            message: 'This provider name is already taken. Please choose another name.'
          }]);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          setErrors([{
            field: 'general',
            message: 'An unexpected error occurred. Please try again later.'
          }]);
        }
      }
    }
  };

  const getFieldError = (fieldName: string) => {
    return errors.find(error => error.field === fieldName)?.message;
  };

  // Move the validateField call to onBlur instead
  const handleBlur = (fieldName: keyof FormData) => {
    validateField(fieldName, formData[fieldName]);
  };


  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Submit New Provider</h1>

      <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
        <TextInput
          label="Submitted By"
          value={currentUser?.email || ''}
          onChange={()=>(null)}
          disabled
          readOnly
        />

        <TextInput
          label="Provider Name"
          value={formData.provider_name}
          onChange={(e) => handleChange('provider_name', e.target.value)}
          onBlur={() => handleBlur('provider_name')}
          error={getFieldError('provider_name')}
          touched={touchedFields.has('provider_name')}
          required
        />

        <TextInput
          label="Provider ID"
          value={convertToSnakeCase(formData.provider_name)}
          onChange={() => null}
          disabled
          readOnly
        />

        <TextInput
          label="Provider Website"
          value={formData.provider_link}
          onChange={(e) => handleChange('provider_link', e.target.value)}
          onBlur={() => validateField('provider_link', formData.provider_link)}
          error={getFieldError('provider_link')}
          touched={touchedFields.has('provider_link')}
          type="text"
          inputMode="url"
          aria-label="Website URL"
          required
        />

        <div className="space-y-2">
          <label className="block text-sm font-medium">Provider Logo</label>
          <div className="relative">
            <div className="w-32 h-32 bg-gray-200 rounded-lg m-4 relative" />
            <ImageUpload 
              onFileSelect={handleLogoSelect}
              currentImage={logoUrl || formData.provider_logo}
              maxSize={500}
              imgClasses="absolute top-0 left-0 w-32 h-32 rounded-lg m-4"
            />
          </div>
          {logoUploadStatus === 'uploading' && (
            <p className="text-blue-500 text-sm">Uploading logo...</p>
          )}
          {logoUploadStatus === 'error' && (
            <p className="text-red-500 text-sm">Failed to upload logo. Please try again.</p>
          )}
        </div>

        <TextInput
          label="Instagram Handle (Optional)"
          value={formData.provider_instagram}
          onChange={(e) => handleOptionalChange('provider_instagram', e.target.value)}
          placeholder="@username"
        />

        <TextInput
          label="TikTok Handle (Optional)"
          value={formData.providers_tiktok}
          onChange={(e) => handleOptionalChange('providers_tiktok', e.target.value)}
          placeholder="@username"
        />

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Country </label>
            <Dropdown
              trigger={
                <div className="w-full p-2 border rounded-lg bg-white">
                  {formData.provider_country || 'Select Country'}
                </div>
              }
              items={COUNTRIES.map(country => ({
                label: country,
                onClick: () => handleCountryChange(country)
              }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Region </label>
            <Dropdown
              trigger={
                <div className="w-full p-2 border rounded-lg bg-white">
                  {formData.provider_region || 'Select Region'}
                </div>
              }
              items={availableRegions.map(region => ({
                label: region,
                onClick: () => handleOptionalChange('provider_region', region)
              }))}
              disabled={!formData.provider_country}
            />
          </div>
          <TextInput
            label="Post Code (Optional)"
            value={formData.provider_postcode}
            onChange={(e) => handleOptionalChange('provider_postcode', e.target.value)}
          />
        </div>

        <TextArea
          label="Provider Description"
          value={formData.provider_description}
          onChange={(e) => handleChange('provider_description', e.target.value)}
          onBlur={() => validateField('provider_description', formData.provider_description)}
          error={getFieldError('provider_description')}
          touched={touchedFields.has('provider_description')}
          placeholder="Describe your service or organization..."
          required
        />

        <EventTypeSelect
          selectedTypes={formData.provider_types}
          onChange={(types) => handleChange('provider_types', types)}
          error={getFieldError('provider_types')}
          touched={touchedFields.has('provider_types')}
        />

        <Button
          className="w-full"
          onClick={handleSubmit}
          disabled={logoUploadStatus === 'uploading'}
        >
          {logoUploadStatus === 'uploading' ? "Uploading Logo..." : "Submit Provider"}
        </Button>
      </form>

      {showConfetti && (
        <ReactConfetti
          width={windowDimensions.width}
          height={windowDimensions.height}
          recycle={false}
        />
      )}

      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold text-center mb-6">
              Congratulations!
            </h2>
            <p className="text-center mb-6">We have received your submission and will review it shortly.</p>
            <p className="text-center mb-6">Please email us on {EMAIL} if you have any questions.</p>
            <div className="flex flex-col space-y-4">
              <Button
                className="w-full"
                onClick={() => {
                  setShowSuccessModal(false);
                  window.location.href = '/profile';
                }}
              >
                View Profile
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmitProvider; 